// Phase A.2.b · Tour Contacts.
//
// Uses the unified <ContactLinker mode="tour"> for adding contacts:
//   - Global Contact search across the entire database.
//   - Existing-relationship indicator per result.
//   - Explicit "Also link to Organization" checkbox (default OFF) —
//     forwards to the backend's canonical `also_link_organization` flag
//     rather than firing two frontend saves.
//
// Historical `tour_contacts` rows with legacy vocabulary DISPLAY safely;
// any role edit must select a canonical role.

import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Plus, X, Mail, Phone, Link2, ExternalLink, Edit3, Tag } from "lucide-react";
import { customerApi } from "@/lib/entities";
import { tourApi } from "@/lib/tourEntities";
import { apiError } from "@/lib/errors";
import { CANONICAL_ROLES, roleLabel } from "@/lib/relationshipRoles";
import { displayName as safeName } from "@/lib/contactDisplay";
import { bestPhone, telHref } from "@/lib/contactPhone";
import { ContactLinker } from "@/components/ContactLinker";
import { RoleChangeDialog } from "@/components/RoleChangeDialog";
import { ContactFormDialog } from "@/pages/contacts/ContactFormDialog";

const SLOTS = [
  { role: "tour_manager", label: "Tour Manager" },
  { role: "production_manager", label: "Production Manager" },
  { role: "business_manager", label: "Business Manager" },
  { role: "transportation_coordinator", label: "Transportation Contact" },
];

export const TourContactsSection = ({ tour, canWrite }) => {
  const [rows, setRows] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [linkerOpen, setLinkerOpen] = useState(false);
  const [roleDlg, setRoleDlg] = useState(null); // {tc}
  const [editDlg, setEditDlg] = useState(null); // contact
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [tc, c] = await Promise.all([
        tourApi.listContacts(tour.id),
        customerApi.get(tour.customer_id),
      ]);
      // Phase A.2.b T24 · rows now carry the embedded `contact` DTO
      // (same shape as GET /api/customers/{cid}/contact-relationships).
      // No N+1 lookup needed.
      setRows(tc || []);
      setCustomer(c);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("TourContacts load:", e);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour.id]);

  const primaryByRole = useMemo(() => {
    const m = {};
    for (const r of rows) if (!m[r.role]) m[r.role] = r;
    return m;
  }, [rows]);

  const others = useMemo(
    () =>
      rows.filter(
        (r) => !SLOTS.some((s) => s.role === r.role) || primaryByRole[r.role]?.id !== r.id,
      ),
    [rows, primaryByRole],
  );

  const removeAttachment = async (tcId, name = "Contact") => {
    if (
      !window.confirm(
        `Unlink this Contact from the Tour? The Contact record remains.`,
      )
    )
      return;
    try {
      await tourApi.removeContact(tour.id, tcId);
      toast.success(`${name} unlinked from Tour.`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const changeRole = async (tcId, newRole) => {
    try {
      await tourApi.patchContact(tour.id, tcId, { role: newRole });
      toast.success("Role updated.");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409) {
        toast.error("This Contact is already linked to this Tour with that role.");
        await load();
      } else if (e?.response?.status === 422 && detail?.code === "invalid_role") {
        toast.error(`"${roleLabel(detail.given)}" is not a valid role.`);
      } else {
        toast.error(apiError(e));
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-coach-silver rounded-md p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10.5px] uppercase tracking-widest text-steel">
            Primary Roles
          </div>
          {canWrite && (
            <button
              data-testid="tour-contact-link-btn"
              onClick={() => setLinkerOpen(true)}
              className="inline-flex items-center gap-1 text-[12px] bg-coach-blue hover:bg-deep-road-navy text-white px-2 py-1 rounded-sm focus-visible:outline-2 focus-visible:outline-touring-lime"
            >
              <Link2 size={12} /> Link Contact
            </button>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          {SLOTS.map((slot) => {
            const attached = primaryByRole[slot.role];
            const contact = attached?.contact;
            return (
              <div
                key={slot.role}
                data-testid={`tour-contact-slot-${slot.role}`}
                className="border border-coach-silver rounded-sm p-3"
              >
                <div className="text-[11px] uppercase tracking-wide text-steel mb-1">
                  {slot.label}
                </div>
                {attached && contact ? (
                  <ContactRowCompact
                    contact={contact}
                    tc={attached}
                    canWrite={canWrite}
                    onOpenChangeRole={(tc) => setRoleDlg({ tc: { ...tc, contact } })}
                    onEditPerson={setEditDlg}
                    onView={(cid) => navigate(`/contacts/${cid}`)}
                    onRemove={removeAttachment}
                  />
                ) : attached ? (
                  <ContactRowCompact
                    contact={{ id: attached.contact_id, first_name: "Contact", last_name: attached.contact_id.slice(0, 8) }}
                    tc={attached}
                    canWrite={canWrite}
                    onOpenChangeRole={(tc) => setRoleDlg({ tc: { ...tc, contact: null } })}
                    onEditPerson={setEditDlg}
                    onView={(cid) => navigate(`/contacts/${cid}`)}
                    onRemove={removeAttachment}
                  />
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="text-[12px] text-steel italic">Not Added</span>
                    {canWrite && (
                      <button
                        data-testid={`tour-contact-add-${slot.role}`}
                        onClick={() => setLinkerOpen({ suggestedRole: slot.role })}
                        className="inline-flex items-center gap-1 text-[12px] text-coach-blue hover:underline focus-visible:outline-2 focus-visible:outline-coach-blue rounded-sm"
                      >
                        <Plus size={12} /> Link Contact
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-4 pt-3 border-t border-coach-silver">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10.5px] uppercase tracking-widest text-steel">
              Other Contacts
            </div>
            {canWrite && (
              <button
                data-testid="tour-contact-add-other"
                onClick={() => setLinkerOpen(true)}
                className="inline-flex items-center gap-1 text-[12px] text-coach-blue hover:underline focus-visible:outline-2 focus-visible:outline-coach-blue rounded-sm"
              >
                <Plus size={12} /> Link Other Contact
              </button>
            )}
          </div>
          {others.length === 0 ? (
            <div className="text-[12px] text-steel">None attached.</div>
          ) : (
            <ul className="divide-y divide-coach-silver/60 border border-coach-silver/60 rounded-sm">
              {others.map((r) => {
                const contact = r.contact;
                return (
                  <li
                    key={r.id}
                    data-testid={`tour-contact-other-${r.id}`}
                    className="px-3 py-2"
                  >
                    <ContactRowCompact
                      contact={contact || { id: r.contact_id, first_name: "Contact" }}
                      tc={r}
                      canWrite={canWrite}
                      onOpenChangeRole={(tc) => setRoleDlg({ tc: { ...tc, contact } })}
                      onEditPerson={setEditDlg}
                      onView={(cid) => navigate(`/contacts/${cid}`)}
                      onRemove={removeAttachment}
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {linkerOpen && (
        <ContactLinker
          mode="tour"
          tourId={tour.id}
          customer={customer}
          onClose={() => setLinkerOpen(false)}
          onLinked={async (opts) => {
            await load();
            if (!opts?.silent) setLinkerOpen(false);
          }}
        />
      )}
      {roleDlg && (
        <RoleChangeDialog
          open={!!roleDlg}
          currentRole={roleDlg.tc.role}
          personName={safeName(roleDlg.tc.contact)}
          onClose={() => setRoleDlg(null)}
          onSave={async (newRole) => {
            await changeRole(roleDlg.tc.id, newRole);
            setRoleDlg(null);
          }}
          testidPrefix="tour-contact-role"
        />
      )}
      {editDlg && (
        <ContactFormDialog
          open={!!editDlg}
          editing={editDlg}
          onClose={() => setEditDlg(null)}
          onSaved={async () => {
            setEditDlg(null);
            await load();
          }}
        />
      )}
    </div>
  );
};

function ContactRowCompact({
  contact, tc, canWrite, onOpenChangeRole, onEditPerson, onView, onRemove,
}) {
  const name = safeName(contact);
  const phone = bestPhone(contact);
  const phoneHref = telHref(phone);
  return (
    <div className="flex flex-wrap sm:flex-nowrap items-start gap-3">
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-carbon truncate font-medium">
          {name}
          {contact.title && (
            <span className="ml-2 text-[11px] font-normal text-steel">
              · {contact.title}
            </span>
          )}
          <span className="ml-2 text-[10.5px] uppercase tracking-widest text-steel">
            · {roleLabel(tc.role)}
          </span>
        </div>
        <div className="text-[11.5px] text-steel truncate">
          {contact.primary_email || "—"}
          {contact.mobile ? ` · ${contact.mobile}` : ""}
        </div>
        {contact.primary_organization_name && (
          <div className="text-[11.5px] text-steel truncate">
            {contact.primary_organization_name}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 shrink-0">
        {contact.id && (
          <button
            type="button"
            onClick={() => onView(contact.id)}
            data-testid={`tour-contact-view-${tc.id}`}
            className="inline-flex items-center gap-1 text-[11.5px] text-coach-blue hover:underline rounded-sm focus-visible:outline-2 focus-visible:outline-coach-blue"
            aria-label={`View ${name}`}
          >
            <ExternalLink size={11} /> View Person
          </button>
        )}
        {canWrite && contact.id && (
          <button
            type="button"
            onClick={() => onEditPerson(contact)}
            data-testid={`tour-contact-edit-${tc.id}`}
            className="inline-flex items-center gap-1 text-[11.5px] text-coach-blue hover:underline rounded-sm focus-visible:outline-2 focus-visible:outline-coach-blue"
            aria-label={`Edit ${name}`}
          >
            <Edit3 size={11} /> Edit Person
          </button>
        )}
        {contact.primary_email && (
          <a
            href={`mailto:${contact.primary_email}`}
            className="text-coach-blue hover:text-deep-road-navy"
            title={`Email ${name}`}
            aria-label={`Email ${name}`}
            data-testid={`tour-contact-email-${tc.id}`}
          >
            <Mail size={14} />
          </a>
        )}
        {phoneHref && (
          <a
            href={phoneHref}
            className="lg:hidden text-coach-blue hover:text-deep-road-navy"
            title={`Call ${name}`}
            aria-label={`Call ${name}`}
            data-testid={`tour-contact-call-${tc.id}`}
          >
            <Phone size={14} />
          </a>
        )}
        {canWrite && (
          <button
            type="button"
            onClick={() => onOpenChangeRole(tc)}
            className="inline-flex items-center gap-1 border border-coach-silver text-[11.5px] text-carbon px-2 py-1 rounded-sm hover:bg-road-white focus-visible:outline-2 focus-visible:outline-coach-blue"
            data-testid={`tour-contact-change-role-${tc.id}`}
            aria-label="Change role"
          >
            <Tag size={11} /> Change Role
          </button>
        )}
        {canWrite && (
          <button
            data-testid={`tour-contact-remove-${tc.id}`}
            onClick={() => onRemove(tc.id, name)}
            title="Unlink Contact"
            aria-label={`Unlink ${name}`}
            className="text-critical-red hover:text-critical-red/70 shrink-0"
          >
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

export default TourContactsSection;
